import { Injectable, inject } from '@angular/core';
import { TokenService } from '../services/token.service';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, finalize, map, of, switchMap, tap, throwError } from 'rxjs';
import { authResponseDTO } from './models/authResponseDTO';
import { BaseEndpointService } from '../interfaces/IEndpoint';
import { Router } from '@angular/router';
import { getErrorMessage } from './models/errorMap';

export const status = {
  successful: 'SUCCESSFUL',
  failed: 'FAILED',
  notStarted: 'NOT STARTED',
  refreshing: 'IN PROGRESS'
} as const;

type StatusValues = typeof status[keyof typeof status];


interface RefreshResult {
  isRefreshing: boolean;
  status: StatusValues;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseEndpointService {
  private readonly tokenService = inject(TokenService);
  private readonly router = inject(Router);
  public readonly isLoggedIn$ = new BehaviorSubject(false);

  public readonly isRefreshingToken$ = new BehaviorSubject<RefreshResult>({isRefreshing: false, status:'NOT STARTED'});

  constructor() {

    super({route: 'auth'})
  }

  public get isLoggedIn() {
    return this.isLoggedIn$.value;
  }

  public set isLoggedIn(value: boolean) {
    this.isLoggedIn$.next(value);
  }

  isAuthenticated(): Observable<boolean> {

    return this.http.get<{ isAuthenticated: boolean }>(this.endpoint + '/verify').pipe(
      map(response => response.isAuthenticated),
      tap(isAuthenticated => this.isLoggedIn$.next(isAuthenticated))
    );
  }


  login(model: {email:string, password:string}) {
    return this.http
      .post<authResponseDTO>(this.endpoint + '/login', {
      ...model
      })
      .pipe(
        tap((result) => {
          this.tokenService.saveAccessToken(result.token);
          this.tokenService.saveRefreshToken(result.refreshToken);
          this.isLoggedIn$.next(true);
        }),
        catchError(error => this.handleError(error))
      );
  }

  refreshToken() {
    let token = this.tokenService.getAccessToken();
    let refreshToken = this.tokenService.getRefreshToken();

    this.isRefreshingToken$.next({isRefreshing: true, status: 'IN PROGRESS'});

    if(token == null || refreshToken == null)
    {
      this.isRefreshingToken$.next({isRefreshing: false, status:'FAILED'});
      return this.logout();
    }

    return this.http
      .post<authResponseDTO>(this.endpoint + `/token/refresh`,{})
      .pipe(
        tap((result) => {
          this.tokenService.saveAccessToken(result.token);
          this.tokenService.saveRefreshToken(result.refreshToken);
          this.isRefreshingToken$.next({isRefreshing: false, status:'SUCCESSFUL'});
          this.isLoggedIn$.next(true);
        }
       ),
       catchError(error => {

        this.isRefreshingToken$.next({isRefreshing: false, status:'FAILED'});
        return this.logout();
      }),
      finalize(() =>  this.isRefreshingToken$.next({isRefreshing: false, status:'NOT STARTED'}))
      );
  }

  logout()
  {
    this.isLoggedIn$.next(false);
    this.tokenService.clearAccessToken();
    this.tokenService.clearRefreshToken();
    this.router.navigateByUrl('/login');

    return this.http.get(this.endpoint + '/logout');
  }

  handleError(errorRes)
  {
    let errorMessage = 'An unknown error occurred!'
    console.log(errorRes)
    const errorDetail = errorRes.error != null ? errorRes.error.replaceAll(" ", "").split(":")[1] : null

    if (errorDetail) {
      errorMessage = getErrorMessage(errorDetail)
    }

    return throwError(() => new Error(errorMessage));
  }

}
